// The point tracker's data rules, without React: which group each of our
// skaters belongs to on the game day, what their row says, and the gauge's scale.

import GameStatus from '../../core/model/gameStatus';
import { abbrevOfTeam, type TeamAbbrev } from '../../core/model/teams';
import type { Game, Player, Skater } from '../../core/model/types';
import { formatGameClock, formatStartLabel, type Translate } from './gameViewModel';
import { isPlayerWithheld } from './todayViewModel';

const FINLAND_GAUGE_TOP = 25;
const DEFAULT_GAUGE_TOP = 25;
const GAUGE_HISTORY_DAYS = 30;
const GAME_START_CLOCK = '00:00';
// Injury levels from here up mean the player is not expected to play (OUT, IR); DTD players often do.
const OUT_LEVEL = 2;

export interface TrackerRow {
    player: Skater;
    // "Lehkonen A": there can be two players of the same name on the list.
    name: string;
}

export interface TrackerPlayedRow extends TrackerRow {
    goals: number;
    assists: number;
    // "COL-CGY 5-2", empty when the game is not on the day's list.
    game: string;
    // Elapsed game time of a live game.
    clock?: string;
}

export interface TrackerUpcomingRow extends TrackerRow {
    // "COL-CGY"
    game: string;
    startTime: string;
    // Start time, "starts soon", postponed or to be decided.
    start?: string;
}

export interface PointTrackerModel {
    played: TrackerPlayedRow[];
    notPlaying: TrackerRow[];
    upcoming: TrackerUpcomingRow[];
    // Some players are left out because their game is hidden.
    withheld: boolean;
}

interface TrackerContext {
    players: readonly Player[];
    // The game day's games.
    games: readonly Game[];
    date: string;
    hiddenTeams: readonly string[];
    revealed: ReadonlySet<string>;
    teamAbbrev?: TeamAbbrev;
    t: Translate;
    now: Date;
}

const compareNames = (a: TrackerRow, b: TrackerRow) => a.name.localeCompare(b.name, 'fi');

// Games are written with the teams' abbreviations, whatever names the game has.
const teamsOf = (game: Game, teamAbbrev: TeamAbbrev) => ({
    home: teamAbbrev(game.homeTeamId) ?? game.homeTeam,
    away: teamAbbrev(game.awayTeamId) ?? game.awayTeam,
});

const teamsLabel = (game: Game, teamAbbrev: TeamAbbrev) => {
    const { home, away } = teamsOf(game, teamAbbrev);
    return `${home}-${away}`;
};

const scoreLabel = (game: Game, teamAbbrev: TeamAbbrev) =>
    `${teamsLabel(game, teamAbbrev)} ${game.homeTeamGoals}-${game.awayTeamGoals}`;

function playedRow(row: TrackerRow, game: Game | undefined, teamAbbrev: TeamAbbrev): TrackerPlayedRow {
    const line = row.player.latestGame;
    const status = GameStatus.deriveStatus(game);
    return {
        ...row,
        goals: line?.goals ?? 0,
        assists: line?.assists ?? 0,
        game: game === undefined ? '' : (status.isNotYetStarted ? teamsLabel(game, teamAbbrev) : scoreLabel(game, teamAbbrev)),
        clock: game !== undefined && status.isLive ? formatGameClock(game) : undefined,
    };
}

// Our skaters of the game day in three groups. A stat line for the day always
// means the player played. Without one, the injury decides until the game is
// under way (the lineup is not known before that), and after that the player is
// not in the lineup.
export function buildPointTracker({ players, games, date, hiddenTeams, revealed, teamAbbrev = abbrevOfTeam, t, now }: TrackerContext): PointTrackerModel {
    const played: TrackerPlayedRow[] = [];
    const notPlaying: TrackerRow[] = [];
    const upcoming: TrackerUpcomingRow[] = [];
    let withheld = false;

    for (const player of players) {
        if (player.kind !== 'skater') {
            continue;
        }
        const hasLine = player.latestGame?.date === date;
        const game = games.find(candidate => candidate.homeTeamId === player.teamId || candidate.awayTeamId === player.teamId);
        if (!hasLine && (game === undefined || !player.isActive)) {
            continue;
        }
        if (isPlayerWithheld(player, games, hiddenTeams, revealed, teamAbbrev)) {
            withheld = true;
            continue;
        }

        const row: TrackerRow = { player, name: `${player.lastName} ${player.firstName.charAt(0)}`.trim() };
        const status = GameStatus.deriveStatus(game);
        const expected = player.injury < OUT_LEVEL;
        if (hasLine || game === undefined) {
            played.push(playedRow(row, game, teamAbbrev));
        } else if (status.isLive && formatGameClock(game) === GAME_START_CLOCK) {
            if (expected) {
                played.push(playedRow(row, game, teamAbbrev));
            } else {
                notPlaying.push(row);
            }
        } else if (status.isLive || status.isFinal || !expected) {
            notPlaying.push(row);
        } else {
            upcoming.push({ ...row, game: teamsLabel(game, teamAbbrev), startTime: game.startTime, start: formatStartLabel(game, status, t, now) });
        }
    }

    return {
        played: played.sort((a, b) => b.goals - a.goals || (b.goals + b.assists) - (a.goals + a.assists) || compareNames(a, b)),
        notPlaying: notPlaying.sort(compareNames),
        upcoming: upcoming.sort((a, b) =>
            new Date(a.startTime).getTime() - new Date(b.startTime).getTime() || a.game.localeCompare(b.game, 'fi') || compareNames(a, b)),
        withheld,
    };
}

// The top of the gauge's scale. Finland's is fixed around its 20 points target.
// Others go up to their best recent day, or today's points when those are more,
// rounded up so that the five bands stay whole numbers. `history` holds the days
// before the shown one, `points` is undefined while the day's total is withheld,
// so that the scale gives nothing away.
export function gaugeTop(country: string, history: readonly number[], points: number | undefined): number {
    if (country === 'FIN') {
        return FINLAND_GAUGE_TOP;
    }
    const best = history.length === 0 ? DEFAULT_GAUGE_TOP : Math.max(...history.slice(-GAUGE_HISTORY_DAYS));
    return Math.max(5, Math.ceil(Math.max(best, points ?? 0) / 5) * 5);
}
